// navigationPlugin.ts
import { VueConstructor } from "vue";
import VueRouter, { Route } from "vue-router";
import store from "../store";

interface NavigationPluginOptions {
	onBack?: (params: { delta: number; from: Route; to: Route }) => void;
	onForward?: (params: { delta: number; from: Route; to: Route }) => void;
}

export const navigationPlugin = {
	install(Vue: VueConstructor, options: NavigationPluginOptions = {}) {
		const { onBack, onForward } = options;

		const setupNavigationTracking = (router: VueRouter) => {
			// console.log("[Navigation Plugin] Setting up navigation tracking");

			// Reset on each event
			store.dispatch("globalState/setNavigation", null);	

			// Store positions for each route
			const routePositions = new Map<string, number>();
			let positionCounter = 0;

			// Initialize first route
			routePositions.set(router.currentRoute.fullPath, positionCounter);

			router.beforeEach((to, from, next) => {
				// console.log("Navigation from:", from.path, "to:", to.path);

				// Reset navigation direction at start of navigation
				store.dispatch("globalState/setNavigation", null);

				// Detect if this is a browser back/forward action
				const isPoppingState = !!(window.history.state && window.history.state.__popState);
				// console.log("Is popping state:", isPoppingState);

				const fromPosition = routePositions.get(from.fullPath) ?? positionCounter;
				// console.log("From position:", fromPosition);

				let toPosition: number;
				if (isPoppingState) {
					// If we're going back to a route we've seen before
					toPosition = routePositions.get(to.fullPath) ?? ++positionCounter;
					// console.log("To position (popstate):", toPosition);

					const delta = fromPosition - toPosition;
					// console.log("Navigation delta:", delta);

					if (delta > 0) {
						// console.log("Setting back navigation");
						store.dispatch("globalState/setNavigation", "back");

						if (onBack) {
							onBack({
								delta,
								from,
								to,
							});
						}
					} else if (delta < 0) {
						// console.log("Setting forward navigation");
						store.dispatch("globalState/setNavigation", "forward");

						if (onForward) {
							onForward({
								delta: Math.abs(delta),
								from,
								to,
							});
						}
					}
				} else {
					// New navigation
					toPosition = ++positionCounter;
					// console.log("To position (new):", toPosition);
				}

				// Store the position for this route
				routePositions.set(to.fullPath, toPosition);

				// Log current route positions for debugging
				// console.log(
				// 	"Current route positions:",
				// 	Array.from(routePositions.entries())
				// 		.map(([path, pos]) => `${path}: ${pos}`)
				// 		.join(", ")
				// );

				// Update history state
				const currentState = window.history.state || {};
				window.history.replaceState(
					{
						...currentState,
						position: toPosition,
					},
					""
				);

				next();
			});

			// Listen for popstate events to mark browser navigation
			window.addEventListener("popstate", (event) => {
				if (event.state) {
					event.state.__popState = true;
					// console.log("Popstate event:", event.state);
				}
			});
		};

		Vue.prototype.$setupNavigationTracking = setupNavigationTracking;
	},
};
